import { Component, OnInit } from "@angular/core";
import { ComponentBase } from "../../models/ComponentBase";
import { faChevronCircleLeft } from "@fortawesome/free-solid-svg-icons";
import { ActivatedRoute, Router } from "@angular/router";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import {
    GetESGListResponse,
    GetESGResponse,
    UpsertESGRequest,
} from "../../WebApiClient";
import { ToastService } from "../../services/toast.service";
import { ESGService } from "../../services/esg.service";
import { BehaviorSubject, Observable } from "rxjs";
import { distinctUntilChanged, map } from "rxjs/operators";
import { SortDirection, SortEvent } from "../../models/ngSortable";
import {
    IconDefinition,
    faSortAmountDown,
    faSortAmountUp,
} from "@fortawesome/free-solid-svg-icons";
import { BlockNavigationIfChange } from "../../guards/block-onchanged.guard";

type ESGQueryKeys = "page" | "sort" | "direction";
type ESGQueryParams = {
    [k in ESGQueryKeys]?: string;
};

@Component({
    selector: "app-sustainability-esg",
    templateUrl: "./sustainability-esg.component.html",
    styleUrls: [
        "./sustainability-esg.component.scss",
        "../../style/sidebarLayout.scss",
        "../../style/form-table.scss",
    ],
    standalone: false,
})
export class SustainabilityESGComponent
    extends ComponentBase
    implements OnInit, BlockNavigationIfChange
{
    faBack: IconDefinition = faChevronCircleLeft;
    showEditor: boolean = false;
    newEsg: boolean = false;

    yearList: number[] = [];

    public readonly isPreviousPagePossible: Observable<boolean>;
    public readonly isNextPagePossible: Observable<boolean>;

    cardHeader: string = "Edit ESG";

    esgForm: FormGroup = new FormGroup({
        year: new FormControl(null, Validators.required),
    });

    sortDirection: SortEvent | undefined = {
        column: "year",
        direction: "desc",
    };
    sortingBehaviorSubject = new BehaviorSubject<SortEvent>({
        column: "year",
        direction: "desc",
    });

    public readonly response: Observable<GetESGListResponse | undefined>;
    public readonly esgList: Observable<GetESGResponse[] | undefined>;

    public inputValid: boolean = false;

    public esgSelected: UpsertESGRequest | undefined;

    constructor(
        private readonly activatedRoute: ActivatedRoute,
        private readonly router: Router,
        private readonly esgService: ESGService,
        private readonly toastService: ToastService
    ) {
        super();

        const queryParams =
            activatedRoute.queryParams as Observable<ESGQueryParams>;

        let pageObservable = queryParams.pipe(
            map((params) => {
                let newPage = Math.max(1, Number(params.page ?? 1));
                let sort = params.sort ?? "year";
                let direction = params.direction ?? "desc";

                this.sortDirection = {
                    column: sort,
                    direction: direction as SortDirection,
                };

                return {
                    page: newPage,
                    sort: this.sortDirection,
                };
            }),
            distinctUntilChanged(
                (a, b) => JSON.stringify(a) === JSON.stringify(b)
            )
        );

        this.response = this.esgService.getESGListObservable(pageObservable);
        this.subscribeTo(this.response, (r) => console.log("response: ", r));

        this.isNextPagePossible = this.response.pipe(
            map((r) =>
                r &&
                r.page &&
                r.pageTotal &&
                Number(r.page) < Number(r.pageTotal)
                    ? true
                    : false
            )
        );
        this.isPreviousPagePossible = queryParams.pipe(
            map((qp) => (qp.page ? Number(qp.page) > 1 : false))
        );

        this.esgList = this.response.pipe(map((r) => r?.esgList));

        this.hasChanges = () => false;

        this.esgForm.valueChanges.subscribe(() => {
            this.hasChanges = () => {
                return this.esgForm.dirty && this.esgForm.touched;
            };
        });
    }

    hasChanges: () => Observable<boolean> | Promise<boolean> | boolean;

    back() {
        this.router.navigate(["/sustainability"]);
    }

    ngOnInit(): void {
        const currentYear = new Date().getFullYear();
        this.yearList = Array.from({ length: 11 }, (_, i) => currentYear - i);
    }

    edit(esg: GetESGResponse): void {
        this.esgSelected = esg;

        this.esgForm.patchValue({
            year: esg.year,
        });

        this.showEditor = true;
        this.cardHeader = "Edit ESG";
        this.newEsg = false;
    }

    cancel() {
        this.showEditor = false;
        this.esgSelected = undefined;

        this.esgForm.reset();
    }

    add() {
        this.esgSelected = this.esgService.newESG();

        this.showEditor = true;
        this.cardHeader = "Add ESG";
        this.newEsg = true;
    }

    onSort({ column, direction }: SortEvent) {
        this.sortDirection = {
            column: column,
            direction: direction,
        };

        this.sortingBehaviorSubject.next({ column, direction });

        this.navigate({ sort: this.sortDirection });
    }
    getSortDirection(column: string): any {
        if (this.sortDirection && this.sortDirection.column === column) {
            return this.sortDirection.direction;
        }
        return "";
    }

    getSortDirectionIcon(column: string): IconDefinition | undefined {
        if (this.sortDirection && this.sortDirection.column === column) {
            return this.sortDirection.direction === "asc"
                ? faSortAmountDown
                : faSortAmountUp;
        }
        return undefined;
    }

    scopeFormValid(valid: boolean) {
        this.inputValid = valid;
    }

    scopeDataUpdated(esgResponse: GetESGResponse) {
        this.esgSelected = esgResponse;
    }

    async onEsgFormSubmit() {
        if (!this.esgForm.valid || !this.esgSelected) {
            return;
        }

        this.esgSelected.year = this.esgForm.get("year")?.value;

        await this.esgService
            .upsert(this.esgSelected)
            .then(() => {
                this.toastService.add(
                    "Saved success",
                    "ESG report has been updated",
                    "success"
                );

                this.cancel();
            })
            .catch(() => {
                this.toastService.add(
                    "Saved failed",
                    "Something went wrong. Year may already be in use. Please try again.",
                    "danger"
                );
            });
    }

    async delete() {
        if (!this.esgSelected || !this.esgSelected.id) {
            return;
        }

        var isSure = confirm("Are you sure you want to delete the ESG report?");
        if (!isSure) {
            return;
        }

        await this.esgService
            .delete(this.esgSelected.id)
            .then(() => {
                this.toastService.add(
                    "Deleted success",
                    "ESG report has been deleted",
                    "success"
                );

                this.cancel();
            })
            .catch(() => {
                this.toastService.add(
                    "Delete failed",
                    "Something went wrong. Please try again.",
                    "danger"
                );
            });
    }

    scopeFormIsDirty($event: any) {
        this.hasChanges = () => $event;
    }

    private navigate(
        query: Partial<{
            page: number;
            keyword: string | null;
            sort: SortEvent | null;
            hideZeroQuantity: number | null;
        }>
    ) {
        const qp2 = {
            ...this.activatedRoute.snapshot.queryParams,
        } as ESGQueryParams;
        if (query.page !== undefined) {
            if (query.page <= 1) {
                delete qp2.page;
            } else {
                qp2.page = query.page.toString();
            }
        }

        if (query.sort !== undefined) {
            if (query.sort === null) {
                delete qp2.sort;
                delete qp2.direction;
            } else {
                qp2.sort = query.sort.column;
                qp2.direction = query.sort.direction;
            }
        }

        this.router.navigate([], {
            queryParams: qp2,
            relativeTo: this.activatedRoute,
            queryParamsHandling: "",
        });
    }
}
